import { type MapOutcome, type MapStatus, outcomeOf } from '../../sessionRows.js';
import type { ChainStep, Session, SessionStep, WorkflowDetail } from '../../workflows.types.js';

export type ChainCard = ChainStep;
export type AlisonRowStatus = MapStatus | 'failed';

export interface AlisonActionDisplay {
  title: string;
  auxiliary: boolean;
  description: string;
  runningTitle: string;
  runningSteps: string[];
}

export interface AlisonStep extends SessionStep {
  alisonCall: string | null;
}

export interface AlisonSession extends Omit<Session, 'steps' | 'asides'> {
  steps: AlisonStep[];
  failure: string | null;
}

export interface AlisonRow {
  actionType: string;
  title: string;
  auxiliary: boolean;
  runId: string | null;
  itemCount: number | null;
  // What the service says the step is doing right now, when it reports it.
  progress: string | null;
  status: AlisonRowStatus;
  summary: string | null;
  alisonCall: string | null;
  runningTitle: string;
  runningSteps: string[];
  outcome: MapOutcome | null;
}

export type PillState =
  | { kind: 'entry' }
  | { kind: 'running'; current: number; total: number }
  | { kind: 'done'; outcome: string; count: number | null }
  | { kind: 'failed'; title: string };

// The route to the goal the page was opened for, else the one Alison would choose, else the
// service's longest route from the page.
export function chainPath(
  cards: ChainCard[],
  workflowId: string,
  goal: string | null,
): string[] | null {
  const paths = cards.flatMap((card) =>
    card.entryPoints
      .filter((entryPoint) => entryPoint.workflowId === workflowId)
      .map((entryPoint) => ({ goal: card.workflowId, path: entryPoint.path })),
  );
  const toGoal = paths.find((candidate) => candidate.goal === goal);
  if (toGoal !== undefined) return toGoal.path;
  if (!cards.some((card) => card.workflowId === workflowId)) return null;
  const recommended = recommendedSteps(cards, [workflowId]);
  if (recommended.length > 0) return [workflowId, ...recommended];
  const longest = paths.toSorted((left, right) => right.path.length - left.path.length)[0];
  return longest?.path ?? [workflowId];
}

// The service's ends after the route, minus those that revisit a step on it or need params the
// route was set up without (a Banner storyboard after a Video brief).
// ponytail: drops a revisiting shortest path instead of rerouting; none exists in today's graph,
// ask the service for route-aware paths if one appears.
export function pathOptions(
  cards: ChainCard[],
  route: string[],
  params: Record<string, unknown> = {},
): string[][] {
  const source = cards.find((card) => card.workflowId === route.at(-1));
  return (source?.offeredPaths ?? [])
    .filter(
      ({ path, params: needed }) =>
        !path.some((step) => route.includes(step)) &&
        Object.entries(needed).every(([key, value]) => (params[key] ?? value) === value),
    )
    .map(({ path }) => path);
}

// The params the route's steps need (a Banner storyboard's brief type). Its first step must send
// them: left unset, the service runs its default type and the end refuses the brief.
export function routeParams(cards: ChainCard[], route: string[]): Record<string, unknown> {
  return Object.assign(
    {},
    ...route.flatMap((step, index) =>
      (cards.find((card) => card.workflowId === step)?.offeredPaths ?? [])
        .filter(({ path }) => path.every((next, at) => route[index + 1 + at] === next))
        .map(({ params }) => params),
    ),
  );
}

// The service's preferred step after the route's last one, among those it offers, so a route is
// the same on every run.
export function recommendedOption(
  cards: ChainCard[],
  route: string[],
  params: Record<string, unknown> = {},
): string[] | null {
  const source = cards.find((card) => card.workflowId === route.at(-1));
  const options = pathOptions(cards, route, params);
  for (const target of source?.recommendedNext ?? []) {
    const option = options.find((path) => path.at(-1) === target);
    if (option !== undefined) return option;
  }
  return null;
}

// "Let Alison choose": the recommended step after `route`, then after that, to the end.
export function recommendedSteps(
  cards: ChainCard[],
  route: string[],
  params: Record<string, unknown> = {},
): string[] {
  const steps: string[] = [];
  for (
    let next = recommendedOption(cards, route, params);
    next !== null;
    next = recommendedOption(cards, [...route, ...steps], params)
  ) {
    steps.push(...next);
  }
  return steps;
}

// Alison's way on once the step at `index` finished and ruled out the one she planned: the edge
// the service recommends off its result, then her route from there. Null when the planned step
// was not Alison's to change, or the result leaves her nothing to recommend.
export function reroute(
  cards: ChainCard[],
  path: string[],
  index: number,
  offered: { actionType: string; enabled: boolean; recommended: boolean }[],
): string[] | null {
  const route = path.slice(0, index + 1);
  if (recommendedOption(cards, route)?.[0] !== path[index + 1]) return null;
  const next = offered.find((edge) => edge.enabled && edge.recommended)?.actionType;
  if (next === undefined || next === path[index + 1]) return null;
  return [...route, next, ...recommendedSteps(cards, [...route, next])];
}

// The step to add without asking: the only one `route` can go on to.
export function onlyOption(
  cards: ChainCard[],
  route: string[],
  params: Record<string, unknown> = {},
): string[] | null {
  const options = pathOptions(cards, route, params);
  return options.length === 1 ? (options[0] ?? null) : null;
}

export function reachableActions(cards: ChainCard[], root: string): string[] {
  const byId = new Map(cards.map((card) => [card.workflowId, card]));
  const seen = new Set<string>();
  const pending = [root];
  for (let current = pending.pop(); current !== undefined; current = pending.pop()) {
    if (seen.has(current) || !byId.has(current)) continue;
    seen.add(current);
    pending.push(...(byId.get(current)?.nextActions ?? []));
  }
  return [...seen];
}

export function actionDisplay(card: ChainCard, detail: WorkflowDetail): AlisonActionDisplay {
  const submit = detail.steps.find((step) => step.kind === 'config')?.submit;
  return {
    title: detail.steps.find((step) => step.kind === 'result')?.title ?? detail.name,
    auxiliary: card.auxiliary,
    description: card.description,
    runningTitle: submit?.runningTitle ?? '',
    runningSteps: submit?.runningSteps ?? [],
  };
}

export function displaysOf(
  cards: ChainCard[],
  details: WorkflowDetail[],
): Record<string, AlisonActionDisplay> {
  return Object.fromEntries(
    details.flatMap((detail) => {
      const card = cards.find((candidate) => candidate.workflowId === detail.workflowId);
      return card === undefined ? [] : [[detail.workflowId, actionDisplay(card, detail)]];
    }),
  );
}

export function fillTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (placeholder, key: string) => {
    const value = values[key];
    return value === undefined ? placeholder : String(value);
  });
}

export function runRows(
  path: string[],
  session: AlisonSession | null,
  displays: Record<string, AlisonActionDisplay>,
  progressOf: (actionType: string) => string | null = () => null,
): AlisonRow[] {
  return path.map((actionType) => {
    const display = displays[actionType];
    const step = session?.steps.find((entry) => entry.actionType === actionType);
    const status = rowStatus(step);
    return {
      actionType,
      title: display?.title ?? actionType,
      auxiliary: display?.auxiliary ?? false,
      runId: step?.runId ?? null,
      itemCount: step?.status === 'completed' ? step.itemCount : null,
      progress: status === 'active' ? progressOf(actionType) : null,
      status,
      summary: step?.summary ?? null,
      alisonCall: step?.alisonCall ?? null,
      runningTitle: display?.runningTitle ?? '',
      runningSteps: display?.runningSteps ?? [],
      outcome: step?.status === 'completed' ? outcomeOf(session?.steps ?? [], step) : null,
    };
  });
}

function rowStatus(step: AlisonStep | undefined): AlisonRowStatus {
  if (step === undefined) return 'pending';
  if (step.status === 'completed') return 'done';
  return step.status === 'failed' ? 'failed' : 'active';
}

export function builtRows(rows: AlisonRow[]): AlisonRow[] {
  return rows.filter((row) => row.status === 'done');
}

// The stages a person follows: auxiliary steps fold into the stage they support.
export function stageRows(rows: AlisonRow[]): AlisonRow[] {
  const stages = rows.filter((row) => !row.auxiliary);
  return stages.length === 0 ? rows : stages;
}

export function stepProgress(rows: AlisonRow[]): { current: number; total: number } {
  const stages = stageRows(rows);
  const done = builtRows(stages).length;
  return { current: Math.min(done + 1, stages.length), total: stages.length };
}

export function isFinished(session: AlisonSession | null): boolean {
  return session?.status === 'completed' || session?.status === 'awaiting_decision';
}

export function pillState(
  path: string[] | null,
  session: AlisonSession | null,
  rows: AlisonRow[],
): PillState | null {
  if (session === null) return path === null ? null : { kind: 'entry' };
  if (session.status === 'failed') {
    const stuck = rows.find((row) => row.status !== 'done');
    return { kind: 'failed', title: stuck?.title ?? '' };
  }
  if (isFinished(session)) {
    const last = builtRows(rows).at(-1);
    return { kind: 'done', outcome: last?.title ?? '', count: last?.itemCount ?? null };
  }
  return { kind: 'running', ...stepProgress(rows) };
}
